// Web Push subscription helper for the mobile PWA.
//
// Flow:
//   1. ensurePushPermission() — asks the browser for Notification permission
//   2. subscribeToPush() — uses the active service worker to create a
//      PushSubscription with our VAPID public key, then upserts it into
//      Supabase `device_tokens` so the Edge Function can target the user.
//   3. unsubscribeFromPush() — local SW unsubscribe + DB delete.
//
// The VAPID public key is shipped via REACT_APP_VAPID_PUBLIC_KEY.

import { supabase, supabaseConfigured } from './supabase';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission() {
  return typeof Notification !== 'undefined' ? Notification.permission : 'default';
}

export async function ensurePushPermission() {
  if (!pushSupported()) throw new Error('Push not supported on this device');
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') {
    throw new Error('Notifications blocked — change in browser settings.');
  }
  const result = await Notification.requestPermission();
  if (result !== 'granted') throw new Error('Notification permission was not granted');
  return result;
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function subscribeToPush() {
  if (!pushSupported()) throw new Error('Push not supported');
  if (!VAPID_PUBLIC_KEY) throw new Error('VAPID public key not configured');
  if (!supabaseConfigured) throw new Error('Supabase not configured');

  await ensurePushPermission();
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    }));

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');

  const json = sub.toJSON();
  const { error } = await supabase.from('device_tokens').upsert(
    {
      user_id: user.id,
      provider: 'webpush',
      endpoint: json.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      user_agent: navigator.userAgent.slice(0, 200),
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'endpoint' }
  );
  if (error) throw error;
  return sub;
}

export async function unsubscribeFromPush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await sub.unsubscribe();
  if (supabaseConfigured) {
    await supabase.from('device_tokens').delete().eq('endpoint', sub.endpoint);
  }
}

export async function isSubscribed() {
  if (!pushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return Boolean(sub);
  } catch {
    return false;
  }
}
